import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { OverlayRef, Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { CdkPortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export interface DropdownItem {
    id?: string | number;
    value?: string | number;
    name?: {
        en: string;
        fr: string;
    };
    label?: string;
    [key: string]: any;
}

@Component({
    selector: 'app-searchable-dropdown',
    templateUrl: './searchable-dropdown.component.html',
    styleUrls: ['./searchable-dropdown.component.scss'],
})
export class SearchableDropdownComponent implements OnInit, OnDestroy, OnChanges {
    @Input() label: string;

    @Input() placeholder: string;

    @Input() items: DropdownItem[] = [];

    @Input() selectedItems: DropdownItem[] = [];

    @Input() multiSelect = false;

    @Input() showAddOption = false;

    @Input() addOptionLabel = '';

    @Input() currentLang = 'en';

    @Input() loading = false;

    @Input() showSelectedInInput = true;

    @Output() itemSelect = new EventEmitter<DropdownItem>();

    @Output() itemDeselect = new EventEmitter<DropdownItem>();

    @Output() addNew = new EventEmitter<string>();

    @Output() createNew = new EventEmitter<any>();

    @ViewChild('dropdownReference') dropdownReference: ElementRef;

    @ViewChild(CdkPortal) contentTemplate: CdkPortal;

    @ViewChild('searchInput') searchInputElement: ElementRef;

    searchControl = new FormControl('');

    filteredItems: DropdownItem[] = [];

    isDropdownVisible = false;

    isInputFocused = false;

    // Flag to determine if we're in search mode (showing input) vs display mode (showing selected items)
    isSearchMode = false;

    protected overlayRef: OverlayRef;

    private searchSubscription: Subscription;

    private backdropSubscription: Subscription;

    private isSelectingItem = false;

    constructor(
        protected overlay: Overlay,
        private sanitizer: DomSanitizer,
    ) {
        this.searchSubscription = this.searchControl.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe((value: string) => {
            this.onSearchChange(value);
        });
    }

    ngOnInit(): void {
        this.filteredItems = [...this.items];
    }

    ngOnChanges(changes: SimpleChanges): void {
        // If items or selectedItems change, update filteredItems
        if (changes.items && !changes.items.firstChange) {
            this.filteredItems = [...this.items];
            this.filterItems(this.searchControl.value || '');
        }
    }

    ngOnDestroy(): void {
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
        if (this.backdropSubscription) {
            this.backdropSubscription.unsubscribe();
        }
        if (this.overlayRef) {
            this.overlayRef.dispose();
        }
    }

    onSearchChange(query: string): void {
        // Just filter items locally instead of emitting an event
        this.filterItems(query);

        // Always maintain search mode when searching
        this.isSearchMode = true;

        // Ensure dropdown is open when searching
        if (!this.isDropdownVisible) {
            this.showDropdown();
        }
    }

    filterItems(query: string): void {
        let filteredList: DropdownItem[] = [];

        if (!query) {
            // When clearing search, start with all items from the original list
            filteredList = [...this.items];

            // Always ensure all selected items are included in the filtered list
            // This is critical to not lose selected items that might not be in the original list
            if (this.selectedItems.length > 0) {
                // Create a map of existing items to avoid duplicates
                const existingItemsMap = new Map();

                filteredList.forEach((item) => {
                    let key = null;
                    if (item.id !== undefined) {
                        key = `id:${String(item.id)}`;
                    } else if (item.value !== undefined) {
                        key = `value:${String(item.value)}`;
                    }
                    if (key) {
                        existingItemsMap.set(key, true);
                    }
                });

                // Add any selected items that aren't already in the items array
                const selectedItemsToAdd = this.selectedItems.filter((item) => {
                    let key = null;
                    if (item.id !== undefined) {
                        key = `id:${String(item.id)}`;
                    } else if (item.value !== undefined) {
                        key = `value:${String(item.value)}`;
                    }
                    return key && !existingItemsMap.has(key);
                });

                // Combine all items with any missing selected items
                filteredList = [...filteredList, ...selectedItemsToAdd];
            }
        } else {
            // Filter items by search query
            const searchQuery = this.normalizeString(query.toLowerCase());

            filteredList = this.items.filter((item) => {
                // Support both traditional and new format
                if (item.label !== undefined) {
                    return this.normalizeString(item.label.toLowerCase()).includes(searchQuery);
                }
                return this.normalizeString(item.name?.[this.currentLang]?.toLowerCase() || '').includes(searchQuery) || this.normalizeString(item.name?.en?.toLowerCase() || '').includes(searchQuery);
            });

            // Always ensure selected items are in the list, even if they don't match the search
            if (this.selectedItems.length > 0) {
                // Create a map of already shown items to avoid duplicates
                const alreadyShownItems = new Map();

                filteredList.forEach((item) => {
                    let key = null;
                    if (item.id !== undefined) {
                        key = `id:${String(item.id)}`;
                    } else if (item.value !== undefined) {
                        key = `value:${String(item.value)}`;
                    }
                    if (key) {
                        alreadyShownItems.set(key, true);
                    }
                });

                // Add any selected items that aren't already in the search results
                const selectedItemsToAdd = this.selectedItems.filter((item) => {
                    let key = null;
                    if (item.id !== undefined) {
                        key = `id:${String(item.id)}`;
                    } else if (item.value !== undefined) {
                        key = `value:${String(item.value)}`;
                    }
                    return key && !alreadyShownItems.has(key);
                });

                // Add selected items that aren't already in the list
                filteredList = [...filteredList, ...selectedItemsToAdd];
            }
        }

        // Now separate selected and unselected items, and put selected items at the top
        const selectedItemIds = new Set(this.selectedItems.map((item) => String(item.id || item.value)));

        // Divide filtered items into selected and unselected
        const selectedItems: DropdownItem[] = [];
        const unselectedItems: DropdownItem[] = [];

        filteredList.forEach((item) => {
            const itemId = String(item.id || item.value);
            if (selectedItemIds.has(itemId)) {
                selectedItems.push(item);
            } else {
                unselectedItems.push(item);
            }
        });

        // Set the filteredItems with selected items first, then unselected items
        this.filteredItems = [...selectedItems, ...unselectedItems];
    }

    getHighlightedText(item: DropdownItem): SafeHtml {
        const text = this.getItemDisplayText(item);
        const searchText = this.searchControl.value?.toLowerCase() || '';

        if (!searchText || !text) {
            return this.sanitizer.bypassSecurityTrustHtml(text);
        }

        // Use normalized strings for comparison but preserve original text for display
        const normalizedText = this.normalizeString(text.toLowerCase());
        const normalizedSearchText = this.normalizeString(searchText);

        // Find all occurrences of the search text in the normalized string
        const occurrences: { start: number; end: number }[] = [];
        let idx = normalizedText.indexOf(normalizedSearchText);
        while (idx !== -1) {
            occurrences.push({
                start: idx,
                end: idx + normalizedSearchText.length,
            });
            idx = normalizedText.indexOf(normalizedSearchText, idx + 1);
        }

        if (occurrences.length === 0) {
            return this.sanitizer.bypassSecurityTrustHtml(text);
        }

        // Build highlighted HTML by mapping occurrences from normalized text to original text
        let result = '';
        let lastEnd = 0;

        for (const occurrence of occurrences) {
            result += text.substring(lastEnd, occurrence.start);
            result += `<span class="highlight">${text.substring(occurrence.start, occurrence.end)}</span>`;
            lastEnd = occurrence.end;
        }

        result += text.substring(lastEnd);

        return this.sanitizer.bypassSecurityTrustHtml(result);
    }

    /**
     * Normalizes a string by removing diacritical marks (accents)
     */
    private normalizeString(str: string): string {
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }

    private escapeRegExp(string: string): string {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    toggleDropdown(): void {
        if (this.isDropdownVisible) {
            this.hideDropdown();
            this.isInputFocused = false;
            this.isSearchMode = false;
        } else {
            // Ensure the search input is properly reset when reopening
            this.isSearchMode = true;

            // When reopening, we may want to clear the previous search query
            // to allow for a fresh search (optional based on UX requirements)
            if (this.selectedItems.length > 0) {
                this.searchControl.setValue('', { emitEvent: false });
            }

            this.showDropdown();
        }
    }

    showDropdown(): void {
        if (this.isDropdownVisible) {
            return;
        }

        this.isSearchMode = true;
        this.overlayRef = this.overlay.create(this.getOverlayConfig());
        this.overlayRef.attach(this.contentTemplate);
        this.syncWidth();
        this.backdropSubscription = this.overlayRef.backdropClick().subscribe(() => {
            this.hideDropdown();
        });
        this.isDropdownVisible = true;

        // Enable wheel scrolling by attaching a wheel event listener to the dropdown
        setTimeout(() => {
            const panel = document.querySelector('.bciti-select-list-overlay .dropdown-panel');
            if (panel) {
                panel.addEventListener('wheel', (e: WheelEvent) => {
                    // Prevent the wheel event from propagating to parent elements
                    e.stopPropagation();
                });
            }
        }, 50);

        // Apply filter to maintain selected items in the filtered items list
        this.filterItems(this.searchControl.value || '');

        // Focus the input element after a short delay to ensure the DOM is ready
        setTimeout(() => {
            if (this.searchInputElement) {
                this.searchInputElement.nativeElement.focus();
                this.isInputFocused = true;
            }
        }, 50);
    }

    hideDropdown(): void {
        if (this.overlayRef) {
            this.overlayRef.detach();
        }
        if (this.backdropSubscription) {
            this.backdropSubscription.unsubscribe();
        }
        this.isDropdownVisible = false;
        this.isSearchMode = false;
    }

    syncWidth(): void {
        if (!this.overlayRef || !this.dropdownReference) return;

        const refRect = this.dropdownReference.nativeElement.getBoundingClientRect();
        this.overlayRef.updateSize({ width: refRect.width });
    }

    protected getOverlayConfig(): OverlayConfig {
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(this.dropdownReference.nativeElement)
            .withPush(true)
            .withPositions([
                {
                    originX: 'start',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'top',
                    offsetY: 4,
                },
                {
                    originX: 'start',
                    originY: 'top',
                    overlayX: 'start',
                    overlayY: 'bottom',
                    offsetY: -4,
                },
            ]);

        // Use noop strategy instead of close for better wheel scrolling without closing the dropdown
        const scrollStrategy = this.overlay.scrollStrategies.noop();

        return new OverlayConfig({
            positionStrategy,
            scrollStrategy,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            panelClass: ['bciti-select-list-overlay', 'scrollable-dropdown'],
            width: this.dropdownReference.nativeElement.getBoundingClientRect().width,
            maxHeight: '300px',
        });
    }

    isItemSelected(item: DropdownItem): boolean {
        if (!item || !this.selectedItems || this.selectedItems.length === 0) {
            return false;
        }

        // String comparison for IDs to handle numeric IDs stored as strings
        if (item.id !== undefined) {
            return this.selectedItems.some((selected) => selected.id !== undefined && String(selected.id) === String(item.id));
        }

        if (item.value !== undefined) {
            return this.selectedItems.some((selected) => selected.value !== undefined && String(selected.value) === String(item.value));
        }

        return false;
    }

    toggleItemSelection(item: DropdownItem): void {
        if (!item) return;

        // Set flag to indicate item selection is in progress
        this.isSelectingItem = true;

        // Make a deep copy of the item to avoid altering the original
        const itemCopy = { ...item };

        // Ensure proper id format for consistent comparison
        if (itemCopy.id !== undefined) {
            itemCopy.id = String(itemCopy.id);
        }

        if (itemCopy.value !== undefined) {
            itemCopy.value = String(itemCopy.value);
        }

        // Ensure name is properly formatted
        if (itemCopy.name && typeof itemCopy.name === 'object') {
            itemCopy.name = { ...itemCopy.name };
        } else if (itemCopy.label && (!itemCopy.name || typeof itemCopy.name !== 'object')) {
            // Create name object from label if needed
            itemCopy.name = {
                en: itemCopy.label,
                fr: itemCopy.label,
            };
        }

        // The key point is that we don't modify the selectedItems array directly
        // because it's an @Input controlled by the parent component.
        // Instead, we emit events that the parent component will handle.
        if (this.multiSelect) {
            if (this.isItemSelected(itemCopy)) {
                // For deselection, emit the item to remove
                this.itemDeselect.emit(itemCopy);
            } else {
                // For selection, emit the new item to add
                this.itemSelect.emit(itemCopy);
            }
        } else {
            // For single select, replace the current selection
            this.itemSelect.emit(itemCopy);

            // In single select mode, only hide the dropdown if not in search mode
            if (!this.isSearchMode) {
                this.hideDropdown();
            }
        }

        // Refocus the input element to ensure it remains editable
        setTimeout(() => {
            if (this.searchInputElement && this.isSearchMode) {
                this.searchInputElement.nativeElement.focus();
                this.isInputFocused = true;
            }
            this.isSelectingItem = false;
        }, 50);
    }

    getItemDisplayText(item: DropdownItem): string {
        if (!item) {
            return '';
        }

        if (item.label !== undefined) {
            return item.label;
        }

        // Ensure name object exists before accessing properties
        if (!item.name) {
            return '';
        }

        return item.name[this.currentLang] || item.name.en || '';
    }

    onAddNewClick(): void {
        // Pass the search term to the createNew event
        this.createNew.emit(this.searchControl.value);
        this.hideDropdown();
    }

    onCreateNewClick(): void {
        this.createNew.emit();
        this.hideDropdown();
    }

    onInputFocus(): void {
        this.isInputFocused = true;
        this.isSearchMode = true;
        this.showDropdown();
    }

    onInputBlur(): void {
        // Use setTimeout to allow click events to process first
        // Only set isInputFocused to false if we're not currently selecting an item
        setTimeout(() => {
            if (!this.isSelectingItem) {
                this.isInputFocused = false;
                // Keep isSearchMode true until dropdown is closed
            }
        }, 100);
    }
}
